import { useRef, useCallback } from "react";
import { ReactGanttRef, Task } from "@dhx/react-gantt";

export interface UseGanttProps {
  onTaskDblClick?: (task: Task) => void;
  onAddTask?: (parentTask: Task | null) => void;
  onTaskUpdate?: (task: Task, mode: string) => void;
  onProgressUpdate?: (task: Task) => void;
  onTaskReorder?: (items: { id: number; sequence_order: number }[]) => void;
  onLinkAdd?: (link: any) => void;
  onLinkDelete?: (linkId: number) => void;
  onTaskDragWarning?: (task: Task, mode: string, warnings: string[], title: string) => void;
  isReadOnly?: boolean;
}

export const useGantt = ({
  onTaskDblClick,
  onAddTask,
  onTaskUpdate,
  onProgressUpdate,
  onTaskReorder,
  onLinkAdd,
  onLinkDelete,
  onTaskDragWarning,
  isReadOnly,
}: UseGanttProps) => {
  const ganttRef = useRef<ReactGanttRef>(null);
  const suppressLinkDeleteRef = useRef(false);

  const setZoom = useCallback((level: string) => {
    const ganttInstance = ganttRef.current?.instance;
    if (!ganttInstance) return;

    let scales: { unit: string; step: number; format: string }[] = [];
    let scale_height = 60;

    switch (level) {
      case "day":
        scales = [
          { unit: "month", step: 1, format: "%M %Y" },
          { unit: "day", step: 1, format: "%d %M" },
        ];
        break;
      case "week":
        scales = [
          { unit: "month", step: 1, format: "%M %Y" },
          { unit: "week", step: 1, format: "Wk #%W" },
        ];
        break;
      case "month":
        scales = [
          { unit: "year", step: 1, format: "%Y" },
          { unit: "month", step: 1, format: "%M %Y" },
        ];
        break;
      case "year":
        scales = [{ unit: "year", step: 1, format: "%Y" }];
        break;
      default:
        return;
    }

    ganttInstance.config.scales = scales as any;
    ganttInstance.config.scale_height = scale_height;
    ganttInstance.render();
  }, []);

  const expandAll = useCallback(() => {
    const ganttInstance = ganttRef.current?.instance;
    if (ganttInstance) {
      const action = () => {
        ganttInstance.eachTask((task: any) => {
          ganttInstance.open(task.id);
        });
      };

      if (ganttInstance.batchUpdate) {
        ganttInstance.batchUpdate(action);
      } else {
        action();
        ganttInstance.render();
      }
    }
  }, []);

  const collapseAll = useCallback(() => {
    const ganttInstance = ganttRef.current?.instance;
    if (ganttInstance) {
      const action = () => {
        ganttInstance.eachTask((task: any) => {
          ganttInstance.close(task.id);
        });
      };

      if (ganttInstance.batchUpdate) {
        ganttInstance.batchUpdate(action);
      } else {
        action();
        ganttInstance.render();
      }
    }
  }, []);

  const handleTaskDblClick = useCallback(
    (id: string | number) => {
      if (!onTaskDblClick) return false;

      const ganttInstance = ganttRef.current?.instance as any;
      if (ganttInstance) {
        const task = ganttInstance.getTask(id);
        if (task) {
          onTaskDblClick(task);
        }
      }
      return false;
    },
    [onTaskDblClick]
  );

  const handleBeforeTaskAdd = useCallback(
    (id: string | number, task: Task) => {
      if (!onAddTask) return false;

      const ganttInstance = ganttRef.current?.instance;
      if (ganttInstance) {
        const parentTask = task.parent
          ? ganttInstance.getTask(task.parent)
          : null;

        if (parentTask && parentTask.type === "milestone") {
          return false;
        }

        onAddTask(parentTask);

        setTimeout(() => {
          if (ganttInstance.isTaskExists(id)) {
            ganttInstance.deleteTask(id);
          }
        }, 0);
      }
      return false;
    },
    [onAddTask]
  );

  const handleBeforeTaskDrag = useCallback(
    (id: string | number) => {
      const ganttInstance = ganttRef.current?.instance as any;
      if (!ganttInstance) return true;

      if (isReadOnly) {
        ganttInstance.message({
          type: "error",
          text: "You do not have permission to modify tasks.",
        });
        return false;
      }

      const task = ganttInstance.getTask(id);
      if (task && ganttInstance.isCriticalTask?.(task)) {
        if (isReadOnly) {
          ganttInstance.message({
            type: "error",
            text: "This task is on the critical path and cannot be modified.",
            expire: 5000,
          });
          return false;
        }
        ganttInstance.message({
          type: "warning",
          text: "Warning: This task is on the critical path. Changes may affect the project completion date.",
          expire: 5000,
        });
      }

      return true;
    },
    [isReadOnly]
  );

  const handleAfterTaskDrag = useCallback(
  (id: string | number, mode: string) => {
    const ganttInstance = ganttRef.current?.instance as any;
    if (!ganttInstance) return true;

    const task = ganttInstance.getTask(id);
    if (!task) return true;

    if (mode === "progress" && onProgressUpdate) {
      onProgressUpdate(task);
      return true;
    }

    if (mode === "resize" || mode === "move") {
      const warnings: string[] = [];

      const taskStart = task.start_date ? new Date(new Date(task.start_date).setHours(0,0,0,0)) : null;
      const taskEnd = task.end_date ? new Date(new Date(task.end_date).setHours(0,0,0,0)) : null;

      // check adjacent phase sequence
      if (task.type === "project") {
        const allPhases: Task[] = [];
        ganttInstance.eachTask((t: Task) => {
          if (t.type === "project") allPhases.push(t);
        });
        allPhases.sort((a: any, b: any) =>
          (a.sequence_order ?? 0) - (b.sequence_order ?? 0)
        );

        const currentIndex = allPhases.findIndex((p) => p.id === task.id);
        const prevPhase = currentIndex > 0 ? allPhases[currentIndex - 1] : null;
        const nextPhase = currentIndex < allPhases.length - 1 ? allPhases[currentIndex + 1] : null;

        const prevEnd = prevPhase?.planned_end_date
          ? new Date(new Date(prevPhase.planned_end_date).setHours(0,0,0,0))
          : null;
        const nextStart = nextPhase?.planned_start_date
          ? new Date(new Date(nextPhase.planned_start_date).setHours(0,0,0,0))
          : null;

        const isStartBeforePrevEnd = prevEnd && taskStart && taskStart < prevEnd;
        const isStartAfterNextStart = nextStart && taskStart && taskStart > nextStart;
        const isEndBeforePrevEnd = prevEnd && taskEnd && taskEnd < prevEnd;
        const isEndAfterNextStart = nextStart && taskEnd && taskEnd > nextStart;

        if (isStartBeforePrevEnd || isStartAfterNextStart || isEndBeforePrevEnd || isEndAfterNextStart) {
          warnings.push(
            "The selected planned dates overlap or fall out of sequence with the adjacent phases. Are you sure you want to proceed?"
          );
        }
      }

      if (task.type === "task" || task.type === "milestone") {
        if (task.parent) {
          const parentPhase = ganttInstance.isTaskExists(task.parent)
            ? ganttInstance.getTask(task.parent)
            : null;

          if (parentPhase) {
            const phaseStart = parentPhase.planned_start_date
              ? new Date(new Date(parentPhase.planned_start_date).setHours(0,0,0,0))
              : null;
            const phaseEnd = parentPhase.planned_end_date
              ? new Date(new Date(parentPhase.planned_end_date).setHours(0,0,0,0))
              : null;

            const isStartBeforePhase = phaseStart && taskStart && taskStart < phaseStart;
            const isStartAfterPhase = phaseEnd && taskStart && taskStart > phaseEnd;
            const isEndBeforePhase = phaseStart && taskEnd && taskEnd < phaseStart;
            const isEndAfterPhase = phaseEnd && taskEnd && taskEnd > phaseEnd;

            if (isStartBeforePhase || isStartAfterPhase || isEndBeforePhase || isEndAfterPhase) {
              warnings.push(
                "The selected planned dates fall outside the planned dates of the parent phase. Are you sure you want to proceed?"
              );
            }
          }
        }
      }

      if (warnings.length > 0 && onTaskDragWarning) {
        const warningTitle = task.type === "project" 
          ? "Phase Date Warning" 
          : task.type === "milestone" 
          ? "Stage Gate Date Warning" 
          : "Activity Date Warning";
        onTaskDragWarning(task, mode, warnings, warningTitle);
      } else if (onTaskUpdate) {
        onTaskUpdate(task, mode);
      }
    }

    return true;
  },[onProgressUpdate, onTaskUpdate, onTaskDragWarning]);

  const handleRowDragEnd = useCallback(
    (id: string | number, parentId: string | number, index: number) => {
      const ganttInstance = ganttRef.current?.instance;
      if (!ganttInstance || !onTaskReorder) return;

      const task = ganttInstance.getTask(id);
      if (!task) return;

      const allUpdates: { id: number; sequence_order: number }[] = [];

      ganttInstance.eachTask((task: Task) => {
        const index = ganttInstance.getTaskIndex(task.id);
        allUpdates.push({
          id: Number(task.id),
          sequence_order: index + 1,
        });
      });

      if (allUpdates.length === 0) {
        console.warn("No tasks found to reorder.");
        return;
      }

      onTaskReorder(allUpdates);
    },
    [onTaskReorder]
  );

  const handleAfterLinkAdd = useCallback(
    (id: string | number, link: any) => {
      const ganttInstance = ganttRef.current?.instance as any;

      if (isReadOnly) {
        if (ganttInstance) {
          ganttInstance.message({
            type: "error",
            text: "You do not have permission to add links.",
          });
          suppressLinkDeleteRef.current = true;
          setTimeout(() => {
            if (ganttInstance.isLinkExists(id)) {
              ganttInstance.deleteLink(id);
            }
            suppressLinkDeleteRef.current = false;
          }, 0);
        }
        return false;
      }

      if (!onLinkAdd) return true;

      if (ganttInstance) {
        const sourceTask = ganttInstance.isTaskExists(link.source) ? ganttInstance.getTask(link.source) : null;
        const targetTask = ganttInstance.isTaskExists(link.target) ? ganttInstance.getTask(link.target) : null;
        if (
          (sourceTask && ganttInstance.isCriticalTask?.(sourceTask)) ||
          (targetTask && ganttInstance.isCriticalTask?.(targetTask))
        ) {
          ganttInstance.message({
            type: "warning",
            text: "Warning: This link involves a critical path task. Changes may affect the project completion date.",
          });
        }

        onLinkAdd(link);

        suppressLinkDeleteRef.current = true;
        setTimeout(() => {
          if (ganttInstance.isLinkExists(id)) {
            ganttInstance.deleteLink(id);
          }
          suppressLinkDeleteRef.current = false;
        }, 0);
      }
      return false;
    },
    [onLinkAdd, isReadOnly]
  );

  const handleBeforeLinkDelete = useCallback(
    (id: string | number) => {
      // Skip API call when deleting a temporary link after add
      if (suppressLinkDeleteRef.current) {
        return true;
      }

      const ganttInstance = ganttRef.current?.instance as any;

      if (isReadOnly) {
        ganttInstance?.message({
          type: "error",
          text: "You do not have permission to delete links.",
        });
        return false;
      }

      if (ganttInstance) {
        const link = ganttInstance.getLink(id);
        const isCritical = link && ganttInstance.isCriticalLink?.(link);

        if (isCritical) {
          ganttInstance.message({
            type: "warning",
            text: "Warning: This link is on the critical path. Deleting it may affect the project completion date.",
            expire: 5000,
          });
        }
      }

      if (!onLinkDelete) return true;

      onLinkDelete(Number(id));
      return false;
    },
    [onLinkDelete, isReadOnly]
  );

  return {
    ganttRef,
    setZoom,
    expandAll,
    collapseAll,
    handleTaskDblClick,
    handleBeforeTaskAdd,
    handleBeforeTaskDrag,
    handleAfterTaskDrag,
    handleRowDragEnd,
    handleAfterLinkAdd,
    handleBeforeLinkDelete,
  };

};